/**
 * GSAP + ScrollTrigger setup
 * Standaard beschikbaar in het thema voor scroll-animaties
 *
 * Gebruik:
 * - data-gsap="fade-up" etc. op elementen voor automatische animaties
 * - gsap.to(), gsap.from() etc. direct in je code
 * - gsapHelpers.fadeIn(), gsapHelpers.parallax() etc.
 */

import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

// Maak gsap globaal beschikbaar voor eenvoudig gebruik
window.gsap = gsap;
window.ScrollTrigger = ScrollTrigger;

/***************************************
 * GSAP Helpers — herbruikbare scroll-animaties
 ***************************************/
const gsapHelpers = {
  /**
   * Fade-in animatie bij scroll
   * @param {string|Element} selector - CSS selector of element
   * @param {Object} options - { y, duration, stagger, start }
   */
  fadeIn(selector, options = {}) {
    const {
      y = 40,
      duration = 0.6,
      stagger = 0,
      start = "top 85%",
      ease = "power2.out",
    } = options;

    return gsap.from(selector, {
      opacity: 0,
      y,
      duration,
      stagger,
      ease,
      scrollTrigger: {
        trigger: selector,
        start,
        toggleActions: "play none none none",
      },
    });
  },

  /**
   * Parallax effect — element beweegt langzamer dan scroll
   * @param {string|Element} selector
   * @param {number} speed - negatief = naar beneden, positief = naar boven (bijv. -0.3)
   */
  parallax(selector, speed = -0.3) {
    return gsap.to(selector, {
      yPercent: speed * 100,
      ease: "none",
      scrollTrigger: {
        trigger: selector,
        start: "top bottom",
        end: "bottom top",
        scrub: true,
      },
    });
  },

  /**
   * Parallax + zoom — beeld zoomt subtiel in bij scroll (voor hero's)
   * @param {string|Element} selector
   * @param {number} scaleEnd - eindschaal (bijv. 1.15)
   */
  parallaxZoom(selector, scaleEnd = 1.15) {
    gsap.set(selector, { scale: 1 });
    return gsap.to(selector, {
      scale: scaleEnd,
      ease: "none",
      scrollTrigger: {
        trigger: selector,
        start: "top bottom",
        end: "bottom top",
        scrub: true,
      },
    });
  },

  /**
   * Scale-in bij scroll
   */
  scaleIn(selector, options = {}) {
    const { duration = 0.6, start = "top 85%" } = options;
    return gsap.from(selector, {
      opacity: 0,
      scale: 0.9,
      duration,
      ease: "power2.out",
      scrollTrigger: {
        trigger: selector,
        start,
        toggleActions: "play none none none",
      },
    });
  },

  /**
   * Horizontaal slide-in (links of rechts)
   */
  slideIn(selector, options = {}) {
    const {
      from = "left",
      distance = 80,
      duration = 0.6,
      start = "top 85%",
    } = options;
    const x = from === "left" ? -distance : distance;
    return gsap.from(selector, {
      opacity: 0,
      x,
      duration,
      ease: "power2.out",
      scrollTrigger: {
        trigger: selector,
        start,
        toggleActions: "play none none none",
      },
    });
  },

  /**
   * Pin element tijdens scroll
   */
  pin(selector, options = {}) {
    const { start = "top top", end = "bottom bottom" } = options;
    ScrollTrigger.create({
      trigger: selector,
      start,
      end,
      pin: true,
    });
  },

  /**
   * Reveal animatie — element "ontrolt" van boven naar beneden
   */
  reveal(selector, options = {}) {
    const { duration = 0.8, start = "top 80%" } = options;
    gsap.set(selector, { overflow: "hidden" });
    return gsap.from(selector, {
      height: 0,
      opacity: 0,
      duration,
      ease: "power3.inOut",
      scrollTrigger: {
        trigger: selector,
        start,
        toggleActions: "play none none none",
      },
    });
  },

  /**
   * Image clip reveal — afbeelding onthult (polygon voor betere browser support)
   */
  clipReveal(selector, options = {}) {
    const { direction = "bottom", duration = 1, start = "top 85%" } = options;
    const clipStart =
      direction === "bottom"
        ? "polygon(0% 100%, 100% 100%, 100% 100%, 0% 100%)"
        : direction === "top"
          ? "polygon(0% 0%, 100% 0%, 100% 0%, 0% 0%)"
          : direction === "left"
            ? "polygon(0% 0%, 0% 0%, 0% 100%, 0% 100%)"
            : "polygon(100% 0%, 100% 0%, 100% 100%, 100% 100%)";
    const clipEnd = "polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)";
    const clipEl = typeof selector === "string" ? document.querySelector(selector) : selector;
    if (!clipEl) return;
    const imgWrap = clipEl.querySelector(".reveal-img-wrap") || clipEl;
    gsap.set(imgWrap, { overflow: "hidden" });
    return gsap.fromTo(
      imgWrap,
      { clipPath: clipStart },
      {
        clipPath: clipEnd,
        duration,
        ease: "power3.inOut",
        scrollTrigger: {
          trigger: clipEl,
          start,
          toggleActions: "play none none none",
        },
      }
    );
  },

  /**
   * Image scale reveal — afbeelding zoomt in bij scroll
   */
  scaleReveal(selector, options = {}) {
    const { duration = 1, start = "top 80%", scaleFrom = 1.2 } = options;
    gsap.set(selector, { overflow: "hidden" });
    return gsap.from(selector, {
      scale: scaleFrom,
      duration,
      ease: "power2.out",
      scrollTrigger: {
        trigger: selector,
        start,
        toggleActions: "play none none none",
      },
    });
  },

  /**
   * Scrub clip reveal — reveal gekoppeld aan scrollpositie (polygon)
   */
  scrubClipReveal(selector, options = {}) {
    const { direction = "bottom", end = "bottom top" } = options;
    const clipStart =
      direction === "bottom"
        ? "polygon(0% 100%, 100% 100%, 100% 100%, 0% 100%)"
        : direction === "top"
          ? "polygon(0% 0%, 100% 0%, 100% 0%, 0% 0%)"
          : direction === "left"
            ? "polygon(0% 0%, 0% 0%, 0% 100%, 0% 100%)"
            : "polygon(100% 0%, 100% 0%, 100% 100%, 100% 100%)";
    const clipEnd = "polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)";
    const clipEl = typeof selector === "string" ? document.querySelector(selector) : selector;
    if (!clipEl) return;
    const imgWrap = clipEl.querySelector(".reveal-img-wrap") || clipEl;
    gsap.set(imgWrap, { overflow: "hidden" });
    return gsap.fromTo(
      imgWrap,
      { clipPath: clipStart },
      {
        clipPath: clipEnd,
        ease: "none",
        scrollTrigger: {
          trigger: clipEl,
          start: "top bottom",
          end,
          scrub: 1,
        },
      }
    );
  },

  /**
   * Scrub scale reveal — image zoomt in/uit met scroll
   */
  scrubScaleReveal(selector, options = {}) {
    const { scaleFrom = 1.4, end = "+=500" } = options;
    gsap.set(selector, { overflow: "hidden" });
    return gsap.fromTo(
      selector,
      { scale: scaleFrom },
      {
        scale: 1,
        ease: "none",
        scrollTrigger: {
          trigger: selector,
          start: "top bottom",
          end,
          scrub: 1,
        },
      }
    );
  },

  /**
   * Curtain reveal — horizontale reveal vanuit het midden (polygon)
   */
  curtainReveal(selector, options = {}) {
    const { end = "bottom top" } = options;
    const clipEl = typeof selector === "string" ? document.querySelector(selector) : selector;
    if (!clipEl) return;
    const imgWrap = clipEl.querySelector(".reveal-img-wrap") || clipEl;
    gsap.set(imgWrap, { overflow: "hidden" });
    return gsap.fromTo(
      imgWrap,
      { clipPath: "polygon(50% 0%, 50% 0%, 50% 100%, 50% 100%)" },
      {
        clipPath: "polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)",
        ease: "power2.inOut",
        scrollTrigger: {
          trigger: clipEl,
          start: "top bottom",
          end,
          scrub: 1,
        },
      }
    );
  },

  /**
   * Horizontal wipe — horizontale wipe (polygon)
   */
  wipeReveal(selector, options = {}) {
    const { direction = "left", end = "bottom top" } = options;
    const clipStart =
      direction === "left"
        ? "polygon(0% 0%, 0% 0%, 0% 100%, 0% 100%)"
        : "polygon(100% 0%, 100% 0%, 100% 100%, 100% 100%)";
    const clipEnd = "polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)";
    const clipEl = typeof selector === "string" ? document.querySelector(selector) : selector;
    if (!clipEl) return;
    const imgWrap = clipEl.querySelector(".reveal-img-wrap") || clipEl;
    gsap.set(imgWrap, { overflow: "hidden" });
    return gsap.fromTo(
      imgWrap,
      { clipPath: clipStart },
      {
        clipPath: clipEnd,
        ease: "none",
        scrollTrigger: {
          trigger: clipEl,
          start: "top bottom",
          end,
          scrub: 1,
        },
      }
    );
  },
};

window.gsapHelpers = gsapHelpers;

/***************************************
 * Data-attribute animaties
 * Gebruik: data-gsap="fade-up" data-gsap-delay="0.2"
 ***************************************/
const gsapPresets = {
  "fade-in": { opacity: 0 },
  "fade-up": { opacity: 0, y: 50 },
  "fade-down": { opacity: 0, y: -50 },
  "fade-left": { opacity: 0, x: 50 },
  "fade-right": { opacity: 0, x: -50 },
  "scale-up": { opacity: 0, scale: 0.85 },
  "blur-in": { opacity: 0, filter: "blur(10px)" },
};

function initGsapDataAnimations() {
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

  document.querySelectorAll("[data-gsap]").forEach((el) => {
    const effect = el.dataset.gsap;
    const preset = gsapPresets[effect] ?? gsapPresets["fade-up"];
    const delay = parseFloat(el.dataset.gsapDelay || 0);
    const duration = parseFloat(el.dataset.gsapDuration || 0.6);
    const start = el.dataset.gsapStart || "top 85%";

    gsap.from(el, {
      ...preset,
      duration,
      delay,
      ease: "power2.out",
      scrollTrigger: {
        trigger: el,
        start,
        toggleActions: "play none none none",
      },
    });
  });

  // Image reveal: data-reveal="clip-bottom" | "clip-top" | "scale" | "curtain" | "wipe-left"
  document.querySelectorAll("[data-reveal]").forEach((el) => {
    const type = el.dataset.reveal || "clip-bottom";
    const end = el.dataset.revealEnd || "+=400";
    const [effect, direction] = type.split("-");

    if (effect === "clip") {
      gsapHelpers.clipReveal(el, { direction: direction || "bottom" });
    }
    if (effect === "scale") {
      gsapHelpers.scaleReveal(el, { scaleFrom: parseFloat(el.dataset.revealScale || 1.2) });
    }
    if (type === "curtain") {
      gsapHelpers.curtainReveal(el, { end });
    }
    if (effect === "wipe") {
      gsapHelpers.wipeReveal(el, { direction: direction || "left", end });
    }
  });

  // Scrub reveals: data-reveal-scrub="clip-bottom" | "scale" | "wipe" (gekoppeld aan scroll)
  document.querySelectorAll("[data-reveal-scrub]").forEach((el) => {
    const type = el.dataset.revealScrub || "clip-bottom";
    const end = el.dataset.revealEnd || "+=500";
    const [effect, direction] = type.split("-");

    if (effect === "clip") {
      gsapHelpers.scrubClipReveal(el, { direction: direction || "bottom", end });
    }
    if (effect === "scale") {
      gsapHelpers.scrubScaleReveal(el, {
        scaleFrom: parseFloat(el.dataset.revealScale || 1.4),
        end,
      });
    }
    if (effect === "wipe") {
      gsapHelpers.wipeReveal(el, { direction: direction || "left", end });
    }
  });

  // Stagger variant: data-gsap="stagger-up" op parent
  document.querySelectorAll('[data-gsap="stagger-up"]').forEach((parent) => {
    const children = parent.children;
    const delay = parseFloat(parent.dataset.gsapDelay || 0);
    const stagger = parseFloat(parent.dataset.gsapStagger || 0.1);
    const start = parent.dataset.gsapStart || "top 85%";

    gsap.from(children, {
      opacity: 0,
      y: 30,
      duration: 0.5,
      stagger,
      delay,
      ease: "power2.out",
      scrollTrigger: {
        trigger: parent,
        start,
        toggleActions: "play none none none",
      },
    });
  });
}

/***************************************
 * Init
 ***************************************/
document.addEventListener("DOMContentLoaded", initGsapDataAnimations);

export { gsap, ScrollTrigger, gsapHelpers };
